import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, RotateCcw, Loader2, Archive } from "lucide-react";
import { fmtDateTimeFirm } from "@/lib/dt";

/* M3l · Item 36 + Round 12 · S1/S2/S3 — Recycle Bin page (firm-level).
   S1 Type labels (EN + AR). S2 "When" in DD-MMM-YYYY HH:MM (LTR-isolated) — STEP 3 · B-7c: the firm zone through the
   one formatter (lib/dt; was a fixed Jeddah zone and its own month list). S3 Days-left = floor; rule in a tooltip. */

function fmtJeddah(iso) {
  return iso ? fmtDateTimeFirm(iso) : "—";
}

export default function RecycleBin() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("deleted");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/recycle-bin");
      setData(r.data);
    } catch (e) { setErr(toErrMsg(e, "Load failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const restoreItem = async (binId) => {
    try {
      await api.post(`/recycle-bin/${binId}/restore`);
      toast.success(t("risk.bin.restoreOk"));
      await load();
    } catch (e) {
      const msg = toErrMsg(e);
      // Step 11d · A11: the refusal is matched on its code (the reply's text never carried it)
      if (e?.response?.data?.error_code === "RECYCLE_BIN_RESTORE_FORBIDDEN" || String(msg).includes("RECYCLE_BIN_RESTORE_FORBIDDEN")) {
        toast.error(t("risk.bin.restoreForbidden"));
      } else {
        toast.error(toErrMsg(e, t("risk.bin.restoreFail")));
      }
    }
  };
  // Batch 14 · M16: permanent delete of a binned engagement (Partner / Director / Admin; typed name; a fresh one-time
  // code within 30 days — the 428 step-up flow sends it to the user's own e-mail)
  const [perm, setPerm] = useState(null);
  const [permName, setPermName] = useState("");
  const permanentDelete = async () => {
    if (!perm) return;
    try {
      await api.post(`/recycle-bin/${perm.id}/permanent`, { confirm_name: permName.trim() });
      toast.success(t("risk.bin.permanentDone")); setPerm(null); setPermName(""); await load();
    } catch (e) {
      const code = e?.response?.data?.error_code || "";
      toast.error(code === "RECYCLE_BIN_PERMANENT_NAME_MISMATCH" ? t("risk.bin.permanentNameMismatch") : toErrMsg(e, t("common.failed")));
    }
  };
  const restoreEngagement = async (engId) => {
    try {
      await api.post(`/recycle-bin/engagement/${engId}/restore`);
      toast.success(t("risk.bin.restoreOk"));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("risk.bin.restoreFail"))); }
  };

  const deleted = data?.deleted_items || [];
  const archived = data?.archived_engagements || [];

  return (
    <div className="space-y-4" data-testid="recycle-bin-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-4 w-4" /> {t("risk.bin.title")}
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t("risk.bin.subtitle")}</p>
          {/* Step 11d · A11 (owner 01-Nov-2026): any firm role may restore — said on the screen, as the Help article says */}
          <p className="text-xs text-muted-foreground" data-testid="recycle-restore-who">{t("risk.bin.restoreWho")}</p>
          {data?.purge_mode && <p className="text-xs text-amber-700" data-testid="recycle-purge-mode">{t(data.purge_mode === "dry_run" ? "risk.bin.purgeDryRun" : "risk.bin.purgeActive")}</p>}
          {data?.engagement_window_days && <p className="text-xs text-muted-foreground" data-testid="recycle-engagement-window">{t("risk.bin.engagementWindow").replace("{n}", String(data.engagement_window_days))}</p>}
        </CardHeader>
        <CardContent>
          {err && (
            <div className="mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800"
                 data-testid="bin-err">{err}</div>
          )}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="deleted" data-testid="bin-tab-deleted">
                {t("risk.bin.tabDeleted")} <span className="ms-1 rounded bg-slate-100 px-1 text-[10px]">{deleted.length}</span>
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="bin-tab-archived">
                {t("risk.bin.tabArchived")} <span className="ms-1 rounded bg-slate-100 px-1 text-[10px]">{archived.length}</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="deleted">
              <BinTable rows={deleted} onRestore={restoreItem} t={t} lang={lang} busy={busy}
                        onPermanent={data?.can_permanent_delete ? (r) => { setPerm(r); setPermName(""); } : null} />
            </TabsContent>
            <TabsContent value="archived">
              <BinTable rows={archived.map((r) => ({ ...r, id: r.entity_id,
                                                     entity_type: r.entity_type || "engagement_archived" }))}
                        onRestore={restoreEngagement} t={t} lang={lang} busy={busy}
                        isArchived />
            </TabsContent>
          </Tabs>
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={load} disabled={busy}
                    data-testid="bin-refresh">
              {busy ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="me-1 h-3.5 w-3.5" />}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
      {perm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bin-permanent-dialog">
          <div className="w-full max-w-md space-y-3 rounded bg-white p-4 text-sm shadow dark:bg-slate-900">
            <div className="font-semibold">{t("risk.bin.permanentTitle")}</div>
            <div>{t("risk.bin.permanentBody").replace("{name}", perm.display_name || perm.engagement_name || perm.entity_name || "").replace("{days}", String(perm.days_in_bin ?? 0))}</div>
            <div className="text-xs text-amber-800" data-testid="bin-permanent-otp-note">{t(perm.permanent_needs_otp ? "risk.bin.permanentOtp" : "risk.bin.permanentNoOtp")}</div>
            <input className="w-full rounded border px-2 py-1" value={permName} onChange={(e) => setPermName(e.target.value)} placeholder={perm.display_name || perm.entity_name || ""} data-testid="bin-permanent-name" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setPerm(null)}>{t("common.cancel")}</Button>
              <Button size="sm" variant="destructive" disabled={!permName.trim()} onClick={permanentDelete} data-testid="bin-permanent-confirm">{t("risk.bin.permanentConfirm")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** S1 — map every known entity_type to its human label (EN + AR). */
function typeLabel(code, t) {
  // Prefer an i18n key — if a label exists in the i18n bundle for
  // ``risk.bin.type.<code>`` it wins; otherwise fall back to a
  // Title-Cased version of the code so unknown types still read.
  const k = `risk.bin.type.${code || "unknown"}`;
  const v = t(k);
  if (v && v !== k) return v;
  return String(code || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function BinTable({ rows, onRestore, t, lang, busy, isArchived, onPermanent = null }) {
  const colW = useResizableColumns({ storageKey: "recycle-bin", defaults: [110, 240, 160, 130, 130, 240, 130, 130], mins: [72, 130, 100, 96, 96, 130, 90, 90] });   // Step 11d · add-on
  if (!rows || rows.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground"
                 data-testid="bin-empty">{t("risk.bin.empty")}</div>;
  }
  const daysLeftRule = t("risk.bin.daysLeftRule");   // Batch 15 · N7 (b): 30 days for items, 60 for engagements (EN + AR strings)
  return (
    <div className="overflow-auto rounded border">
      <ResizableTable cols={colW} className="w-full text-[12px]" data-testid="bin-table">
        <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
          <tr>
            <ResizableTh index={0} className="px-2 py-1.5 text-start">{t("risk.bin.colType")}</ResizableTh>
            <ResizableTh index={1} className="px-2 py-1.5 text-start">{t("risk.bin.colName")}</ResizableTh>
            <ResizableTh index={2} className="px-2 py-1.5 text-start">{t("risk.bin.colEng")}</ResizableTh>
            <ResizableTh index={3} className="px-2 py-1.5 text-start">{t("risk.bin.colDeletedBy")}</ResizableTh>
            <ResizableTh index={4} className="px-2 py-1.5 text-start">{t("risk.bin.colDeletedAt")}</ResizableTh>
            <ResizableTh index={5} className="px-2 py-1.5 text-start">{t("risk.bin.colReason")}</ResizableTh>
            <ResizableTh index={6} className="px-2 py-1.5 text-end">{t("risk.bin.colDaysLeft")}</ResizableTh>
            <ResizableTh index={7} className="px-2 py-1.5 text-end">{t("risk.bin.colActions")}</ResizableTh>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => {
            const dLeft = Number.isFinite(r.days_left) ? Math.floor(r.days_left) : 0;
            return (
            <tr key={r.id} data-testid={`bin-row-${r.id}`}>
              <td className="px-2 py-1.5">
                {isArchived ? <Archive className="inline h-3 w-3 me-1" /> : null}
                <span className="text-[11px]" data-testid={`bin-type-${r.id}`}>
                  {typeLabel(r.entity_type, t)}
                </span>
              </td>
              <td className="px-2 py-1.5">{r.entity_name || "—"}</td>
              <td className="px-2 py-1.5 text-xs">{r.engagement_name || r.engagement_id || "—"}</td>
              <td className="px-2 py-1.5">{r.deleted_by_name || "—"}</td>
              <td className="px-2 py-1.5 font-mono text-[11px]"
                  data-testid={`bin-when-${r.id}`}>
                {fmtJeddah(r.deleted_at) /* S2 */}
              </td>
              <td className="px-2 py-1.5 text-xs">{r.delete_reason || "—"}</td>
              <td className="px-2 py-1.5 text-end font-mono text-[11px]"
                  title={daysLeftRule} /* S3 — rule tooltip */
                  data-testid={`bin-days-${r.id}`}>
                {dLeft > 0 ? dLeft : <span className="text-red-600">0</span>}
                {r.window_days ? <span className="ms-1 text-[9px] text-muted-foreground">/ {r.window_days}</span> : null}   {/* Batch 14 · M16: 60 for an engagement */}
              </td>
              <td className="px-2 py-1.5 text-end">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={() => onRestore(r.id)} disabled={busy}
                        data-testid={`bin-restore-${r.id}`}>
                  <RotateCcw className="me-1 h-3 w-3" /> {t("risk.bin.restore")}
                </Button>
                {onPermanent && r.entity_type === "engagement" && !r.purged_at && (   /* Batch 14 · M16 */
                  <Button size="sm" variant="ghost" className="ms-1 h-7 text-[11px] text-red-700" onClick={() => onPermanent(r)} disabled={busy}
                          data-testid={`bin-permanent-${r.id}`}><Trash2 className="me-1 h-3 w-3" /> {t("risk.bin.permanent")}</Button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </ResizableTable>
    </div>
  );
}
